import _, { get, isEmpty, toLower } from "lodash";
import { PostHog } from "posthog-js/react";
import React, { useEffect, useState } from "react";
// Import useState
import { trackPromise } from "react-promise-tracker";

import { cn } from "@shared/ui";
import { ChevronDown } from "lucide-react";
import { DocumentUpload } from "../components/DocumentUpload";
import { Field, inputClassName } from "../components/Field";
import { useCaptureDocumentAttempt } from "../components/report/useCaptureDocumentAttempt";
import {
  AxleServiceError,
  combineImagesToPDF,
  generateSignedUrl,
  processDocuments,
  stubAccount,
  stubPolicy,
  updatePolicy,
  uploadDocument,
} from "../lib/axle";
import { createEvent } from "../lib/ignitionEvents";
import {
  AUTOPILOT_ERROR_MESSAGES_TABLE,
  AutopilotErrorType,
  DOC_AI_UNSUPPORTED_POLICY_TYPES,
  PolicyType,
} from "../lib/constants";
import {
  handleStubAccountError,
  handleStubPolicyError,
} from "../lib/errorHandlersUtility";
import {
  resolveTerminalHttpFailureType,
  retryUploadOperation,
  UploadFileFailureError,
} from "../lib/uploadRetry";
import { compressImage } from "../lib/utility";

// Error message constants for validation
const ERROR_MESSAGES = {
  MULTIPLE_PDFS:
    "You can only upload one PDF file at a time. Please remove additional PDF files.",
  MIXED_FILE_TYPES:
    "You cannot mix PDF files with images. Please upload either a single PDF or multiple images of the same document.",
} as const;

const MAP_FILE_EXT_TO_MIME = {
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
};

type UploadFileSuccess = {
  success: true;
  documentKey: string;
  updatedFilePath: string;
};

type UploadFileFailure = {
  success: false;
  error: UploadFileFailureError;
} & AxleServiceError;

type UploadFileResult = UploadFileSuccess | UploadFileFailure;

interface EnhancedManualPolicyDocumentProps {
  step: string;
  integration?: string;
  nextStep: (step: string) => void;
  accountInfo: any;
  setAccountInfo: (info: any) => void;
  policyInfo: any;
  setPolicyInfo: (info: any) => void;
  loginInformation: any;
  setLoginInformation: (info: any) => void;
  manualRetryCount: number;
  setManualRetryCount: (count: number) => void;
  session: any;
  posthog: PostHog;
  setManualError: (error: string | null) => void;
  setShowNav: (show: boolean) => void;
  client: { id: string; [key: string]: any };
}

const EnhancedManualPolicyDocument = ({
  step,
  integration,
  nextStep,
  accountInfo,
  setAccountInfo,
  policyInfo,
  setPolicyInfo,
  loginInformation,
  setLoginInformation,
  manualRetryCount,
  setManualRetryCount,
  session,
  posthog,
  setManualError,
  setShowNav,
  client,
}: EnhancedManualPolicyDocumentProps) => {
  const ignitionToken = session.id;
  const captureDocumentAttempt = useCaptureDocumentAttempt(
    integration,
    loginInformation
  );
  const policyTypesFromSession = get(session, "config.scopes.policyTypes", []);

  // State for policy type selection
  const [availablePolicyTypes, setAvailablePolicyTypes] = useState(
    policyTypesFromSession
  );
  const [selectedPolicyTypes, setSelectedPolicyTypes] = useState<PolicyType[]>(
    []
  );
  const [showPolicyTypeDropdown, setShowPolicyTypeDropdown] = useState(false);

  // State for multi-file uploads
  const [pendingDocuments, setPendingDocuments] = useState<
    { filePath: string; fileData: any }[]
  >([]);
  const [isMultiFileMode, setIsMultiFileMode] = useState(false);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );

  const loadingSteps = [
    "Uploading your document",
    "Extracting data from your document",
    "Processing extracted data",
  ];

  /**
   * Effect to initialize policy type selection UI based on client configuration
   *
   * This effect determines whether to show a policy type dropdown to the user and sets
   * the default selected policy types based on:
   * 1. Available policy types from the session configuration
   * 2. Environment (dev vs production) - dropdown only shown in dev for certain scenarios
   * 3. Special cases for auto-only and home/condo combinations
   *
   * Dropdown visibility rules:
   * - Always hidden for "auto only" or "home + condo only" configurations
   * - Shown for all other multi-type configurations across all environments
   *
   * Runs whenever the session changes to reconfigure policy type selection.
   */
  useEffect(() => {
    const isEnhancedManual = get(session, "config.manual.enhanced") === true;

    let types = [...policyTypesFromSession].sort();
    if (isEnhancedManual) {
      types = types.filter(
        (t: string) =>
          !DOC_AI_UNSUPPORTED_POLICY_TYPES.includes(t as PolicyType)
      );
    }

    const isOnlyAuto = types.length === 1 && types[0] === "auto";
    const isOnlyHomeCondo =
      types.length === 2 && types.includes("home") && types.includes("condo");

    if (!isOnlyAuto && !isOnlyHomeCondo && types.length > 0) {
      setShowPolicyTypeDropdown(true);
      setAvailablePolicyTypes(types);
      setSelectedPolicyTypes([]); // No default — user must explicitly pick a type
    } else if (types.length > 0) {
      // If dropdown is not shown, still set the default type based on what's available
      // Only select the relevant type(s) based on scenario
      if (isOnlyAuto) {
        setSelectedPolicyTypes(["auto" as PolicyType]);
      } else if (isOnlyHomeCondo) {
        setSelectedPolicyTypes(["home", "condo"] as PolicyType[]);
      } else {
        setSelectedPolicyTypes([types[0] as PolicyType]);
      }
    }
  }, [session, policyTypesFromSession]);

  /**
   * Helper function to validate file upload
   * @param fileData - The file data to validate
   * @returns object with validation result and error details
   */
  const validateFileUpload = (
    fileData: any
  ): { isValid: boolean; error?: string } => {
    if (!fileData) {
      alert("Please upload a document!");
      return { isValid: false, error: "No file data provided" };
    }

    // Add additional validations as needed
    if (fileData.size && fileData.size === 0) {
      alert(
        "The uploaded file appears to be empty. Please try again with a valid document."
      );
      return { isValid: false, error: "Empty file" };
    }

    return { isValid: true };
  };

  /**
   * Helper function to validate file combination
   * @param documents - Array of pending documents
   * @returns object with validation result and error message
   */
  const validateFileCombo = (
    documents: { filePath: string; fileData: any }[]
  ): { isValid: boolean; error?: string } => {
    if (documents.length === 0) {
      return { isValid: true };
    }

    if (documents.length === 1) {
      return { isValid: true };
    }

    // Check if there's a mix of PDF and other files
    const hasPDF = documents.some((doc) =>
      toLower(doc.filePath).includes(".pdf")
    );
    const hasNonPDF = documents.some(
      (doc) => !toLower(doc.filePath).includes(".pdf")
    );

    if (hasPDF && hasNonPDF) {
      return {
        isValid: false,
        error: ERROR_MESSAGES.MIXED_FILE_TYPES,
      };
    }

    // Check if there are multiple PDFs
    const pdfCount = documents.filter((doc) =>
      toLower(doc.filePath).includes(".pdf")
    ).length;
    if (pdfCount > 1) {
      return {
        isValid: false,
        error: ERROR_MESSAGES.MULTIPLE_PDFS,
      };
    }

    return { isValid: true };
  };

  /**
   * Generates a signed URL for S3 upload. Pure helper — no navigation side effects.
   */
  const handleSignedUrlGeneration = async (
    ignitionToken: string,
    policyId: string,
    filePath: string,
    useTemporaryLocation: boolean = false
  ): Promise<
    | { success: true; signedUrl: string; objectKey: string }
    | ({ success: false } & AxleServiceError)
  > => {
    const [signedUrlResponse, signedUrlResponseError] = await generateSignedUrl(
      ignitionToken,
      policyId,
      filePath,
      useTemporaryLocation
    );

    if (signedUrlResponseError) {
      return {
        success: false,
        code: get(signedUrlResponseError, "code", null),
        message: get(signedUrlResponseError, "message", null),
      };
    }

    if (isEmpty(get(signedUrlResponse, "signedUrl"))) {
      console.error("Signed URL is empty");
      return { success: false, code: 500, message: "process" };
    }

    return {
      success: true,
      signedUrl: signedUrlResponse.signedUrl,
      objectKey: signedUrlResponse.objectKey,
    };
  };

  /**
   * Helper function to process image compression if needed
   * Compresses non-PDF files to ensure they meet size and pixel constraints
   * @param fileData - The original file data
   * @param filePath - The file path to determine file type
   * @returns Promise<any> - The processed file data (compressed if image, original if PDF)
   */
  const processImageFile = async (fileData: any, filePath: string) => {
    if (!toLower(filePath).includes(".pdf")) {
      const fileExtension = _.chain(filePath)
        .split(".")
        .last()
        .toLower()
        .value();
      const mimeType = get(MAP_FILE_EXT_TO_MIME, fileExtension);
      const blob = new Blob([fileData], {
        type: mimeType,
      });

      const file = new File([blob], filePath, {
        type: mimeType,
      });

      try {
        // Compress image to at most 40 megapixels and 5 MB
        return await compressImage(file, get(MAP_FILE_EXT_TO_MIME, fileExtension, ""));
      } catch (compressionError) {
        // Compression is a best-effort optimization. Some browser/webview
        // environments cannot create a canvas, which throws here. Skip
        // compression and upload the original image; any real size/type
        // problem surfaces downstream in processDocuments.
        console.error(
          "Image compression failed; uploading uncompressed image:",
          compressionError
        );
        posthog.capture("document-compression-skipped", {
          step,
          filePath,
          mimeType,
          reason: get(compressionError, "message", "unknown"),
        });
        // Return the uncompressed File (not the raw fileData) so the upload
        // PUT keeps the correct image Content-Type instead of octet-stream.
        return file;
      }
    }
    return fileData;
  };

  /**
   * Uploads document to the signed URL with automatic retry. Pure helper — no navigation.
   */
  const handleDocumentUpload = async (
    signedUrl: string,
    fileData: any
  ): Promise<{ success: true } | ({ success: false } & AxleServiceError)> => {
    console.log(`Uploading the document at the signed url...`);
    const [, error] = await retryUploadOperation(() =>
      uploadDocument(signedUrl, fileData)
    );

    if (!error) {
      return { success: true };
    }

    return { success: false, code: error.code, message: error.message };
  };

  const navigateUploadFailure = (
    error: UploadFileFailureError,
    uploadManualErrorKey: string,
    documentRef?: string
  ) => {
    captureDocumentAttempt({ documentRef });
    setShowNav(true);

    switch (error) {
      case "upload":
        setManualError(uploadManualErrorKey);
        nextStep("manual-document-error");
        break;
      case "connection":
        nextStep("connection-error");
        break;
      case "signed-url":
        setManualError("generate upload URL for");
        nextStep("manual-document-error");
        break;
      case "validation":
        break;
      case "unexpected":
        setManualError("process");
        nextStep("manual-document-error");
        break;
    }
  };

  /**
   * Helper function to create documents object
   * Creates a standardized document object structure for API consumption
   * @param filePath - The file path/name of the uploaded document
   * @param documentKey - The unique key/identifier for the document
   * @returns Array containing the formatted document object
   */
  const createDocumentsObject = (fileName: string, documentKey: string) => {
    return [
      {
        source: "user",
        name: fileName,
        key: documentKey,
        createdAt: new Date().toISOString(),
        type: null,
        effectiveDate: null,
        issuedDate: null,
      },
    ];
  };

  /**
   * Helper function to handle process documents errors coming from the autopilot call
   * @param processDocumentsError - The error object from processDocuments API call
   * @param manualRetryCount - Current retry count for error tracking
   * @param setManualRetryCount - Function to update retry count
   * @param setManualError - Function to set error messages
   * @param nextStep - Function to navigate to next step/error page
   */
  const handleProcessDocumentsError = (
    processDocumentsError: any,
    manualRetryCount: number,
    setManualRetryCount: (count: number) => void,
    setManualError: (error: string | null) => void,
    nextStep: (step: string) => void
  ) => {
    console.log(
      "Processing documents via autopilot failed with retry: ",
      manualRetryCount
    );

    setManualRetryCount(manualRetryCount + 1);

    console.log(
      "Error from processDocuments: ",
      JSON.stringify(processDocumentsError)
    );

    switch (get(processDocumentsError, "code")) {
      case 400:
        const errorCode: AutopilotErrorType | undefined = Object.keys(
          AUTOPILOT_ERROR_MESSAGES_TABLE
        ).find(
          (key: string) =>
            AUTOPILOT_ERROR_MESSAGES_TABLE[key as AutopilotErrorType]
              .message === processDocumentsError.message
        ) as AutopilotErrorType | undefined;

        console.log("Error code from processDocuments: ", errorCode);

        switch (errorCode) {
          case "generic-document-error":
          case "option-not-supported":
          case "unsupported-document":
            nextStep("unsupported-document");
            return;
          case "document-type-not-supported-by-client":
          case "document-type-not-supported-by-axle":
            nextStep("document-type-not-supported");
            return;
          case "document-missing-fields":
          case "text-validation-failed":
            nextStep(errorCode);
            return;
          default:
            return;
        }
        break;
      case 500:
        switch (processDocumentsError.message) {
          case "Text extraction failed":
            setManualError("extract text from");
            break;
          case "Failed to split and classify the document":
            setManualError("accurately classify");
            break;
          case "Failed to analyze the document":
            setManualError("analyze text from");
            break;
        }
        nextStep("manual-document-error");
        return;
      default:
        nextStep("connection-error");
        return;
    }
  };

  /**
   * Helper function to handle manual fallback (stub account/policy)
   * Called when process documents fails after retries, creates stub account and policy
   * then updates the policy with the uploaded document
   * @param ignitionToken - Authentication token for API calls
   * @param loginInformation - User login and carrier information
   * @param documentsObject - Array of document objects to attach to policy
   * @param manualRetryCount - Current retry count for error tracking
   * @param setManualRetryCount - Function to update retry count
   * @param setManualError - Function to set error messages
   * @param nextStep - Function to navigate to next step/error page
   * @returns Promise<object|null> - Object with accountId, policyId, and processDetail if successful, null if failed
   */
  const handleManualFallback = async (
    ignitionToken: string,
    loginInformation: any,
    documentsObject: any[],
    manualRetryCount: number,
    setManualRetryCount: (count: number) => void,
    setManualError: (error: string | null) => void,
    nextStep: (step: string) => void
  ) => {
    const processDetail = "enhanced-manual-push-through";

    console.log(
      "Stubbing account and policy, then updating the policy with the document on the 2nd retry..."
    );

    //TODO: remove once we validate which pathway doesn't set ignitionToken (refer to AXL-4235)
    if (isEmpty(ignitionToken)) {
      console.log(`processDetail: ${processDetail}`);
      console.error("No ignitionToken provided"); //Use this .error to search in Sentry
    }

    const [accountStub, stubAccountError] = await stubAccount(
      ignitionToken,
      {
        carrier: get(loginInformation, "carrier.id", "Other"),
      },
      processDetail
    );

    if (stubAccountError) {
      handleStubAccountError(stubAccountError, nextStep, setManualError);
      return null;
    }

    const [policyStub, stubPolicyError] = await stubPolicy(
      ignitionToken,
      {
        account: accountStub.id,
        carrier: get(loginInformation, "carrier.id", "Other"),
      },
      processDetail
    );

    if (stubPolicyError) {
      handleStubPolicyError(stubPolicyError, nextStep, setManualError);
      return null;
    }

    const [, updatePolicyResponseError] = await updatePolicy(
      ignitionToken,
      policyStub.id,
      accountStub.id,
      {
        documents: documentsObject,
        modifiedAt: new Date().toISOString(),
      }
    );

    if (updatePolicyResponseError) {
      console.log("Failed with error... retry count is: ", manualRetryCount);
      setManualRetryCount(manualRetryCount + 1);
      switch (get(updatePolicyResponseError, "code")) {
        case 500:
          console.log(JSON.stringify(updatePolicyResponseError));
          nextStep("axle-error");
          return null;
        default:
          nextStep("connection-error");
          return null;
      }
    }

    return {
      accountId: accountStub.id,
      policyId: policyStub.id,
      processDetail,
    };
  };

  /**
   * Helper function to submit document for processing
   * Handles the document processing workflow after upload is complete
   * @param documentKey - The document key to process
   * @param documentsObject - Array of document objects to process
   * @param firstImageDocumentKey - Key of the first uploaded image for push-through multi-image uploads
   * @returns Promise<void>
   */
  const submitDocument = async (
    documentKey: string,
    documentsObject: any[],
    firstImageDocumentKey?: string
  ) => {
    // Mark this session as having attempted a manual upload, regardless of
    // whether successful or not — used to compute manual-attempt-based conversion metrics
    await createEvent({
      ignitionToken,
      status: "attempted",
      result: "manual",
      resultDetail: "enhanced-manual",
      session,
      client,
    });

    let policyId = policyInfo.id;
    let accountId = accountInfo.id;

    setLoginInformation({
      ...loginInformation,
      result: "manual",
      resultDetail: "enhanced-manual", // Set to enhanced-manual as a placeholder until we get the more detailed detail name back from process documents
    });

    const maxManualRetryCount = get(
      session,
      "config.manual.max-retry-count",
      3
    );

    // Process documents via autopilot, which will handle the classification and
    // validation of the document. Call processDocuments if retry count is < maxManualRetryCount
    if (manualRetryCount < maxManualRetryCount - 1) {
      // Returns account ID, policy ID, upserts documents in processDocuments
      // Pass the updated documentsObject with the selected type
      captureDocumentAttempt({ documentRef: documentKey });
      const [processDocumentsResponse, processDocumentsError] =
        await processDocuments(
          ignitionToken,
          documentKey,
          documentsObject,
          selectedPolicyTypes
        );

      if (processDocumentsError) {
        captureDocumentAttempt({
          documentRef: documentKey,
          documentClassification:
            processDocumentsError.reportContext?.documentClassification,
        });
        handleProcessDocumentsError(
          processDocumentsError,
          manualRetryCount,
          setManualRetryCount,
          setManualError,
          nextStep
        );
        return;
      } else {
        accountId = get(processDocumentsResponse, "accountId", "");
        policyId = get(processDocumentsResponse, "policyId", "");

        setLoginInformation({
          ...loginInformation,
          result: "manual", // Adding result again here because of a race condition, where login information that was set above is not set in time for this call
          resultDetail: processDocumentsResponse.metadata.processDetail,
        });
      }

      // If we have exceeded the manual retry count, update the policy with the document and go to success
    } else {
      const useFirstImageForPushThrough = get(
        session,
        "config.manual.img-for-multi-upload-push-through",
        false
      );

      // Prepend the 1st uploaded image as documents[0]; combined PDF remains at documents[1]
      if (useFirstImageForPushThrough && firstImageDocumentKey) {
        documentsObject = [
          {
            ...get(documentsObject, "[0]", {}),
            key: firstImageDocumentKey,
          },
          ...documentsObject,
        ];
      }

      const manualFallbackResult = await handleManualFallback(
        ignitionToken,
        loginInformation,
        documentsObject,
        manualRetryCount,
        setManualRetryCount,
        setManualError,
        nextStep
      );

      if (!manualFallbackResult) {
        return;
      }

      accountId = manualFallbackResult.accountId;
      policyId = manualFallbackResult.policyId;

      setLoginInformation({
        ...loginInformation,
        resultDetail: manualFallbackResult.processDetail,
      });
    }

    // Setting account and policy info
    setAccountInfo({ id: accountId });
    setPolicyInfo({ id: policyId });

    nextStep("success");
  };

  const uploadFile = async (
    currentFileData: any,
    currentFilePath: string,
    useTemporaryLocation: boolean = false,
    uploadManualErrorKey: string = "upload"
  ): Promise<UploadFileResult> => {
    captureDocumentAttempt();
    try {
      let updatedFileData = currentFileData;
      const updatedFilePath = currentFilePath;

      const validation = validateFileUpload(updatedFileData);
      if (!validation.isValid) {
        setShowNav(true);
        return {
          success: false,
          error: "validation",
          message: validation.error || "File validation failed",
          code: null,
        };
      }

      setShowNav(false);

      const signedUrlResult = await handleSignedUrlGeneration(
        ignitionToken,
        policyInfo.id,
        updatedFilePath,
        useTemporaryLocation
      );

      if (!signedUrlResult.success) {
        const signedUrlErrorKind = resolveTerminalHttpFailureType(
          signedUrlResult.code,
          "signed-url"
        );
        navigateUploadFailure(signedUrlErrorKind, uploadManualErrorKey);
        return {
          success: false,
          error: signedUrlErrorKind,
          message: signedUrlResult.message || "Failed to generate signed URL",
          code: signedUrlResult.code,
        };
      }

      // Compression failures are handled inside processImageFile (logged and
      // skipped), so this never aborts the upload. A throw here would only be a
      // truly unexpected error, which the outer catch handles.
      updatedFileData = await processImageFile(
        updatedFileData,
        updatedFilePath
      );

      const uploadResult = await handleDocumentUpload(
        signedUrlResult.signedUrl,
        updatedFileData
      );

      if (!uploadResult.success) {
        const uploadErrorKind = resolveTerminalHttpFailureType(
          uploadResult.code,
          "upload"
        );
        navigateUploadFailure(
          uploadErrorKind,
          uploadManualErrorKey,
          signedUrlResult.objectKey
        );
        return {
          success: false,
          error: uploadErrorKind,
          message: uploadResult.message || "Failed to upload document",
          code: uploadResult.code,
        };
      }

      return {
        success: true,
        documentKey: signedUrlResult.objectKey,
        updatedFilePath,
      };
    } catch (error: any) {
      console.error("Unexpected error in uploadFile:", error);
      navigateUploadFailure("unexpected", uploadManualErrorKey);
      return {
        success: false,
        error: "unexpected",
        message:
          error.message || "An unexpected error occurred during file upload",
        code: null,
      };
    }
  };

  /**
   * Main upload function that handles the complete document upload and processing workflow
   *
   * This function now supports multi-file uploads from the beginning and validates file combinations.
   *
   * Workflow:
   * 1. Add file to pending documents collection
   * 2. Validate file combination (no PDF + other files, max 1 PDF)
   * 3. If valid single PDF: process immediately
   * 4. If valid images: enable multi-file mode with submit option
   * 5. If invalid combination: show error and disable submit
   *
   * @param currentFileData - The file data (blob/buffer) to upload
   * @param currentFilePath - The file name/path for the uploaded file
   */
  const submitButtonHandler = async (
    currentFileData: any,
    currentFilePath: string
  ) => {
    // Add the new file to pending documents
    const newDocument = {
      filePath: currentFilePath,
      fileData: currentFileData,
    };
    const updatedDocuments = [...pendingDocuments, newDocument];

    setPendingDocuments(updatedDocuments);

    // Validate the file combination
    const validation = validateFileCombo(updatedDocuments);

    if (!validation.isValid) {
      // Invalid combination - show error and enter multi-file mode
      setFileValidationError(validation.error || "Invalid file combination");

      // Track validation error events
      if (validation.error?.includes(ERROR_MESSAGES.MULTIPLE_PDFS)) {
        posthog.capture("document-upload-error-multiple-pdfs", {
          step,
          fileCount: updatedDocuments.length,
          pdfCount: updatedDocuments.filter((doc) =>
            toLower(doc.filePath).includes(".pdf")
          ).length,
        });
      } else if (validation.error?.includes(ERROR_MESSAGES.MIXED_FILE_TYPES)) {
        posthog.capture("document-upload-error-mixed-file-types", {
          step,
          fileCount: updatedDocuments.length,
          fileTypes: updatedDocuments.map((doc) =>
            toLower(doc.filePath).includes(".pdf") ? "pdf" : "image"
          ),
        });
      }

      setIsMultiFileMode(true);
      setShowNav(true);
      return;
    }

    // Clear any previous validation errors
    setFileValidationError(null);

    // Determine if this is a single PDF workflow
    const isPDF = toLower(currentFilePath).includes(".pdf");

    const acceptedDocs = get(session, "config.documentation.acceptedDocs");

    const onlySupportsIdCards =
      acceptedDocs &&
      acceptedDocs.length === 1 &&
      acceptedDocs[0]?.type === "id-card";

    if (isPDF && updatedDocuments.length === 1) {
      // Single PDF workflow - process immediately
      const submitProcess = async () => {
        const uploadFileResponse = await uploadFile(
          currentFileData,
          currentFilePath
        );

        if (!uploadFileResponse.success) {
          console.error(
            "File upload failed:",
            uploadFileResponse.error,
            uploadFileResponse.message
          );
          return;
        }

        const { documentKey, updatedFilePath } = uploadFileResponse;
        const documentsObject = createDocumentsObject(
          updatedFilePath,
          documentKey
        );

        await submitDocument(documentKey, documentsObject);
      };

      try {
        await trackPromise(submitProcess(), "carrier-loader");
      } finally {
        setShowNav(true);
      }
    } else {
      // Multi-file mode (images or ID cards only)
      console.log("Multi-file mode enabled");
      posthog.capture("document-upload-enter-multi-image-mode", {
        step,
        fileCount: updatedDocuments.length,
        fileTypes: updatedDocuments.map((doc) =>
          toLower(doc.filePath).includes(".pdf") ? "pdf" : "image"
        ),
        trigger: "valid_images",
      });
      setIsMultiFileMode(true);
      setShowNav(true);
    }
  };

  /**
   * Helper function to handle multi-file submission
   * Combines multiple uploaded images into a PDF and then submits for processing
   * Only allows submission if file combination is valid
   */
  const handleMultiFileSubmit = async () => {
    // Validate file combination before processing
    const validation = validateFileCombo(pendingDocuments);

    if (!validation.isValid) {
      setFileValidationError(validation.error || "Invalid file combination");
      return;
    }

    // Clear validation error
    setFileValidationError(null);

    // Wrap the entire multi-file submission process with promise tracking for loading UI
    const multiFileProcess = async () => {
      try {
        // Hide nav buttons and show loading
        setShowNav(false);

        let finalDocumentKey: string;
        let finalDocumentName: string;
        let firstImageDocumentKey: string | undefined;

        if (pendingDocuments.length === 1) {
          // Upload single file directly
          const singleFileResponse = await uploadFile(
            pendingDocuments[0].fileData,
            pendingDocuments[0].filePath,
            false,
            "upload file"
          );

          if (!singleFileResponse.success) {
            console.error(
              "Single file upload failed:",
              singleFileResponse.error,
              singleFileResponse.message
            );
            return;
          }

          // Single file: Use existing document key
          finalDocumentKey = singleFileResponse.documentKey;
          finalDocumentName =
            singleFileResponse.updatedFilePath || "single-file";
        } else {
          // Upload all pending files at the same time
          const uploadResponses = await Promise.all(
            pendingDocuments.map(async (doc) => {
              const response = await uploadFile(
                doc.fileData,
                doc.filePath,
                true,
                "upload multiple files"
              );
              return response;
            })
          );

          const failedUpload = uploadResponses.find((res) => !res.success) as
            | UploadFileFailure
            | undefined;
          if (failedUpload) {
            console.error(
              "Multi-file upload failed:",
              failedUpload.error,
              failedUpload.message
            );
            return;
          }

          // Collect all document keys and file names from successful uploads
          const uploadedDocumentKeys = uploadResponses
            .map((res) => (res.success ? res.documentKey : ""))
            .filter(Boolean);

          const uploadedFileNames = uploadResponses.map((res) =>
            res.success ? res.updatedFilePath : "file"
          );

          // Multiple images: Combine into PDF
          const [combinedPDFResponse, combinedPDFError] =
            await combineImagesToPDF(
              ignitionToken,
              uploadedDocumentKeys.map((key: string) => ({ key }))
            );

          if (combinedPDFError) {
            console.error(
              "Failed to combine images into PDF:",
              combinedPDFError
            );
            setManualError("combine images");
            nextStep("manual-document-error");
            return;
          }

          finalDocumentKey = get(combinedPDFResponse, "documentKey");

          if (!finalDocumentKey) {
            console.error("No combined document key received");
            setManualError("combine images");
            nextStep("manual-document-error");
            return;
          }

          finalDocumentName = "Multi-Image User Document";
          firstImageDocumentKey = get(uploadedDocumentKeys, "[0]");
        }

        // Create documents object for the final document
        const documentsObject = createDocumentsObject(
          finalDocumentName,
          finalDocumentKey
        );

        // Submit the final document
        await submitDocument(
          finalDocumentKey,
          documentsObject,
          firstImageDocumentKey
        );
      } catch (error) {
        console.error("Error in multi-file submit:", error);
        setManualError("process files");
        nextStep("manual-document-error");
      }
    };

    await trackPromise(multiFileProcess(), "carrier-loader");

    // Show nav buttons after processing
    setShowNav(true);
  };

  /**
   * Helper function to remove a file from the pending documents list
   * @param index - The index of the file to remove
   */
  const removeUploadedFile = (index: number) => {
    const updatedDocuments = pendingDocuments.filter((_, i) => i !== index);
    setPendingDocuments(updatedDocuments);

    // Re-validate file combination after removal
    const validation = validateFileCombo(updatedDocuments);
    if (validation.isValid) {
      setFileValidationError(null);
    } else {
      setFileValidationError(validation.error || null);
    }

    // If no files left, exit multi-file mode
    if (updatedDocuments.length === 0) {
      setIsMultiFileMode(false);
      setFileValidationError(null);
    }
  };

  /**
   * Helper function to handle multiple file selection at once
   * This provides better handling of multi-file uploads by processing all files in batch
   * @param files - Array of file objects with fileData and filePath
   */
  const handleMultipleFilesSelected = async (
    files: { fileData: ArrayBuffer; filePath: string }[]
  ) => {
    // Add all files to pending documents at once
    const newDocuments = files.map((file) => ({
      filePath: file.filePath,
      fileData: file.fileData,
    }));

    const updatedDocuments = [...pendingDocuments, ...newDocuments];
    setPendingDocuments(updatedDocuments);

    // Validate the file combination
    const validation = validateFileCombo(updatedDocuments);

    if (!validation.isValid) {
      // Invalid combination - show error and enter multi-file mode
      setFileValidationError(validation.error || "Invalid file combination");

      // Track validation error events for batch selection
      if (validation.error?.includes(ERROR_MESSAGES.MULTIPLE_PDFS)) {
        posthog.capture("document-upload-error-multiple-pdfs", {
          step,
          fileCount: updatedDocuments.length,
          trigger: "batch_selection",
        });
      } else if (validation.error?.includes(ERROR_MESSAGES.MIXED_FILE_TYPES)) {
        posthog.capture("document-upload-error-mixed-file-types", {
          step,
          fileCount: updatedDocuments.length,
          trigger: "batch_selection",
        });
      }

      setIsMultiFileMode(true);
      setShowNav(true);
      return;
    }

    // Clear any previous validation errors
    setFileValidationError(null);

    // Check if this is a single PDF that should be processed immediately
    const isPDF =
      files.length === 1 && toLower(files[0].filePath).includes(".pdf");

    if (isPDF && updatedDocuments.length === 1) {
      // Single PDF workflow - process immediately
      const submitProcess = async () => {
        const uploadFileResponse = await uploadFile(
          files[0].fileData,
          files[0].filePath
        );

        if (!uploadFileResponse.success) {
          console.error(
            "File upload failed:",
            uploadFileResponse.error,
            uploadFileResponse.message
          );
          return;
        }

        const { documentKey, updatedFilePath } = uploadFileResponse;
        const documentsObject = createDocumentsObject(
          updatedFilePath,
          documentKey
        );

        await submitDocument(documentKey, documentsObject);
      };

      try {
        await trackPromise(submitProcess(), "carrier-loader");
      } finally {
        setShowNav(true);
      }
    } else {
      // Multi-file mode (images or ID cards only)
      console.log("Multi-file mode enabled with", files.length, "files");
      posthog.capture("document-upload-enter-multi-image-mode", {
        step,
        fileCount: updatedDocuments.length,
        fileTypes: updatedDocuments.map((doc) =>
          toLower(doc.filePath).includes(".pdf") ? "pdf" : "image"
        ),
        trigger: "batch_selection",
      });
      setIsMultiFileMode(true);
      setShowNav(true);
    }
  };

  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog, step]);

  const policyTypeSelector = showPolicyTypeDropdown ? (
    <Field id="policyTypeSelect" label="Policy type">
      <div className="relative">
        <select
          id="policyTypeSelect"
          value={selectedPolicyTypes.length > 0 ? selectedPolicyTypes[0] : ""}
          onChange={(e) => setSelectedPolicyTypes([e.target.value as PolicyType])}
          className={cn(
            inputClassName,
            "appearance-none pr-10",
            selectedPolicyTypes.length === 0 && "text-ink-secondary"
          )}
        >
          <option value="" disabled>
            Select a policy type
          </option>
          {availablePolicyTypes.map((type: string) => (
            <option key={type} value={type} className="text-ink">
              {_.capitalize(type)}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden
          strokeWidth={1.75}
          className="pointer-events-none absolute right-3 top-1/2 size-[18px] -translate-y-1/2 text-ink"
        />
      </div>
    </Field>
  ) : undefined;

  const disableUpload =
    showPolicyTypeDropdown && selectedPolicyTypes.length === 0;

  return (
    <DocumentUpload
      session={session}
      loginInformation={loginInformation}
      loadingSteps={loadingSteps}
      posthog={posthog}
      step={step}
      isMultiImageMode={isMultiFileMode}
      pendingDocumentKeys={pendingDocuments.map(
        (_, index) => `pending-${index}`
      )}
      pendingFileNames={pendingDocuments.map((doc) => doc.filePath)}
      submitButtonHandler={submitButtonHandler}
      onSubmitMultiImage={handleMultiFileSubmit}
      onRemoveFile={removeUploadedFile}
      disableSubmit={!!fileValidationError}
      onMultipleFilesSelected={handleMultipleFilesSelected}
      validationError={fileValidationError}
      additionalContent={policyTypeSelector}
      disableUpload={disableUpload}
      nextStep={nextStep}
    />
  );
};

export default EnhancedManualPolicyDocument;
